/**
 * Users domain — cache invalidation registry.
 *
 * Compiler-enforced: every `UsersMutationId` member needs a registry
 * entry and vice versa.
 */

import type { QueryKey } from '@tanstack/react-query'

import {
  containsKeyFilter,
  type InvalidationRule,
} from '../invalidation/useInvalidatingMutation'
import {
  listInvitationsQueryKey,
  listUsersQueryKey,
} from '../generated/hooks/usersController'

export type UsersMutationId =
  | 'updateUserType'
  | 'inviteUser'
  | 'resendInvitation'
  | 'deleteInvitation'

/**
 * Query keys every users mutation should invalidate. Includes the
 * generated keys plus the legacy string keys still used by hand-written
 * `api.ts` callers during the migration window.
 */
export function commonUserInvalidations(): QueryKey[] {
  return [listUsersQueryKey(), ['users'], ['user-types']]
}

export const invalidationRegistry: Record<
  UsersMutationId,
  InvalidationRule<any>
> = {
  updateUserType: {
    invalidates: () => commonUserInvalidations(),
  },
  inviteUser: {
    invalidates: () => [listInvitationsQueryKey(), ['invitations']],
    removes: () => [containsKeyFilter('invitations')],
  },
  resendInvitation: {
    invalidates: () => [listInvitationsQueryKey(), ['invitations']],
    removes: () => [containsKeyFilter('invitations')],
  },
  deleteInvitation: {
    invalidates: () => [listInvitationsQueryKey(), ['invitations']],
    removes: () => [containsKeyFilter('invitations')],
  },
}
